// A API mora no mesmo domínio do portal em todos os ambientes — quem separa os
// caminhos é o nginx de borda, e em dev o proxy do Vite. Por isso o padrão
// basta, e a mesma imagem do frontend serve a qualquer ambiente.
const BASE_URL = import.meta.env.VITE_API_URL ?? '/api'

/** Métodos que não alteram dado e, por isso, dispensam o token CSRF. */
const METODOS_SEGUROS = new Set(['GET', 'HEAD', 'OPTIONS'])

/** Cookie em que a API entrega o token CSRF, e cabeçalho em que o cobra de volta. */
const COOKIE_CSRF = 'XSRF-TOKEN'
const CABECALHO_CSRF = 'X-XSRF-TOKEN'

/** Erro de borda da API, já com o status HTTP preservado. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Corpo da resposta de erro, quando for JSON. */
    readonly corpo?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Rotas em que o 401 é resposta esperada, e não sessão perdida: a entrada
 * recusada e a leitura da sessão (que devolve "sem sessão").
 */
const ROTAS_SEM_SESSAO_ESPERADA = new Set(['/v1/auth/login', '/v1/auth/sessao'])

const ouvintesDaSessaoPerdida = new Set<() => void>()

/**
 * Avisa quando a API responde 401 numa rota que exigia sessão: a sessão
 * expirou ou foi encerrada com a página aberta. Quem guarda dado da sessão (o
 * cache das consultas) se inscreve aqui para apagá-lo num lugar só, em vez de
 * cada tela tratar o 401 como um erro comum e deixar à vista o que já tinha
 * carregado. Devolve a função que cancela a inscrição.
 */
export function aoPerderASessao(ouvinte: () => void): () => void {
  ouvintesDaSessaoPerdida.add(ouvinte)
  return () => {
    ouvintesDaSessaoPerdida.delete(ouvinte)
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
}

/**
 * Lê o token CSRF que a API deixou no cookie. É o único cookie que o
 * JavaScript enxerga: o de sessão é `HttpOnly` e vai sozinho, pelo navegador.
 */
export function tokenCsrf(cookies: string = document.cookie): string | undefined {
  const par = cookies
    .split(';')
    .map((trecho) => trecho.trim())
    .find((trecho) => trecho.startsWith(`${COOKIE_CSRF}=`))
  return par === undefined ? undefined : decodeURIComponent(par.slice(COOKIE_CSRF.length + 1))
}

/**
 * Cliente HTTP fino e único ponto de saída do frontend. Não engole erro:
 * qualquer status fora de 2xx vira ApiError, que o TanStack Query converte em
 * estado de erro visível na UI.
 *
 * A sessão da Área do Gestor é um cookie, então a requisição vai com
 * `credentials: 'same-origin'` — a API mora no mesmo domínio do portal. Em toda
 * requisição que altera dado o token CSRF segue no cabeçalho; sem ele a API
 * responde 403.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, method = 'GET', ...rest } = options
  // FormData (o envio de arquivo) segue como está: o navegador monta o
  // Content-Type com o boundary, e fixar o nosso quebraria o multipart.
  const formulario = body instanceof FormData
  const json = body !== undefined && !formulario
  const csrf = METODOS_SEGUROS.has(method.toUpperCase()) ? undefined : tokenCsrf()

  const response = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(json ? { 'Content-Type': 'application/json' } : {}),
      ...(csrf === undefined ? {} : { [CABECALHO_CSRF]: csrf }),
      ...headers,
    },
    ...(json ? { body: JSON.stringify(body) } : {}),
    ...(formulario ? { body } : {}),
  })

  if (!response.ok) {
    // O backend responde com include-message: never, então não há detalhe
    // sensível para propagar — usamos o status como fonte da mensagem.
    const corpo: unknown = await response.json().catch(() => undefined)
    if (response.status === 401 && !ROTAS_SEM_SESSAO_ESPERADA.has(path)) {
      ouvintesDaSessaoPerdida.forEach((ouvinte) => {
        ouvinte()
      })
    }
    throw new ApiError(
      response.status,
      `Falha na requisição a ${path} (HTTP ${response.status})`,
      corpo
    )
  }

  if (response.status === 204) {
    return undefined as T
  }
  // Rota que devolve `void` responde 200 sem corpo (a exclusão do tooltip):
  // o sucesso não pode virar erro por não haver JSON para ler.
  const texto = await response.text()
  if (texto === '') {
    return undefined as T
  }
  return JSON.parse(texto) as T
}

/**
 * O nome do arquivo que a API mandou em `Content-Disposition`, ou `undefined`
 * se o cabeçalho não traz um. Lê `filename*` (UTF-8) antes de `filename`.
 */
function nomeDoAnexo(cabecalho: string | null): string | undefined {
  if (cabecalho === null) return undefined
  const codificado = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(cabecalho)?.[1]
  if (codificado !== undefined) {
    try {
      return decodeURIComponent(codificado.trim())
    } catch {
      // cai no `filename` simples
    }
  }
  const simples = /filename\s*=\s*(?:"([^"]*)"|([^;]+))/i.exec(cabecalho)
  const nome = (simples?.[1] ?? simples?.[2])?.trim()
  return nome === undefined || nome === '' ? undefined : nome
}

/** Quanto esperar o navegador pegar o blob antes de liberá-lo. */
const LIBERAR_BLOB_APOS_MS = 10_000

/** A API monta o arquivo inteiro em memória; passado isto, é pendurada, não lenta. */
const LIMITE_DO_DOWNLOAD_MS = 60_000

/**
 * Pede um arquivo à API e o entrega ao navegador como download. Devolve o nome
 * com que o arquivo foi salvo: o de `Content-Disposition`, ou `nomePadrao` se a
 * resposta não o trouxer.
 *
 * Fica ao lado de `apiFetch` porque a resposta não é JSON, mas a regra é a
 * mesma: sessão do mesmo domínio e qualquer status fora de 2xx vira `ApiError`.
 * Download é sempre GET, então não leva o token CSRF.
 */
export async function baixarArquivo(path: string, nomePadrao = 'arquivo'): Promise<string> {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'GET',
    credentials: 'same-origin',
    signal: AbortSignal.timeout(LIMITE_DO_DOWNLOAD_MS),
  })

  if (!response.ok) {
    throw new ApiError(response.status, `Falha na requisição a ${path} (HTTP ${response.status})`)
  }

  const nome = nomeDoAnexo(response.headers.get('Content-Disposition')) ?? nomePadrao
  const url = URL.createObjectURL(await response.blob())
  const ancora = document.createElement('a')
  ancora.href = url
  ancora.download = nome
  ancora.hidden = true
  document.body.append(ancora)
  ancora.click()
  ancora.remove()
  // O clique já entregou o blob ao navegador; liberar na hora cancelaria
  // downloads grandes em alguns navegadores, então espera um instante.
  setTimeout(() => URL.revokeObjectURL(url), LIBERAR_BLOB_APOS_MS)
  return nome
}

/**
 * Envia um evento do uso do mapa público à rota de registro, e esquece.
 *
 * É a única saída HTTP sem a sessão: o registro é anônimo, então vai com
 * `credentials: 'omit'` (nenhum cookie, nem o de sessão de quem também está na
 * gestão) e sem o token CSRF, que a API dispensa só nesta rota. `keepalive`
 * deixa o pedido terminar mesmo com a aba fechando, que é quando o tempo de
 * navegação sai. Não usa `navigator.sendBeacon`: ele manda os cookies e não
 * deixa escolher o cabeçalho.
 *
 * Nenhuma falha (rede, 4xx, 5xx, 429 da borda) chega a quem chamou nem é
 * tentada de novo: contar o uso nunca pode atrapalhar o mapa, e repetir
 * inflaria o contador.
 */
export function enviarUso(evento: object): void {
  try {
    fetch(`${BASE_URL}/v1/uso/eventos`, {
      method: 'POST',
      keepalive: true,
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(evento),
    }).catch(() => undefined)
  } catch {
    // O navegador recusou o pedido antes de enviá-lo (corpo acima do limite do
    // keepalive, por exemplo): segue sem registro.
  }
}
